import { PropsWithChildren } from "react";
import { GroupBlock } from "../../types";
import { i18nLanguage } from "i18n";
import { getLocalizedString } from "../../utils/getLocalizedString";
import { normalizeRichTextHtml } from "../../utils/normalizeRichTextHtml";
import { GroupMediaCarousel } from "./GroupMediaCarousel";

type TripperPlaceGroupProps = PropsWithChildren & {
  group: GroupBlock;
  language: i18nLanguage;
  isActive: boolean;
  footerDistance?: React.ReactNode;
};

const CheckIcon = ({ color = "var(--tripper-text-subtle)" }: { color?: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M7 12.5L10 15.5L17 8.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const TripperPlaceGroup = ({
  group,
  language,
  isActive,
  footerDistance,
  children,
}: TripperPlaceGroupProps) => {
  const groupName = group.name
    ? getLocalizedString(group.name, language)
    : group.place?.name
      ? getLocalizedString(group.place.name, language)
      : "Place";
  const rawDescription = group.description
    ? getLocalizedString(group.description, language)
    : group.place?.description
      ? getLocalizedString(group.place.description, language)
      : "";
  const groupDescription = normalizeRichTextHtml(rawDescription);

  return (
    <div
      className={`tripper-group${isActive ? "" : " tripper-group--inactive"}`}
      data-group-id={group._id}
      data-active={isActive ? "true" : "false"}
    >
      {/* Group header */}
      <div className="tripper-group__header">
        <h3 className="tripper-group__title">{groupName}</h3>
        {groupDescription && (
          <div className="tripper-group__desc" dangerouslySetInnerHTML={{ __html: groupDescription }} />
        )}
        {group.media && (
          <GroupMediaCarousel media={group.media} language={language} />
        )}
      </div>

      {/* Grouped stops */}
      <div className="tripper-group__stops">{children}</div>

      {/* End of group footer */}
      <div className="tripper-group__footer">
        <div className="tripper-group__footer-row">
          <div className="tripper-group__footer-icon">
            <CheckIcon />
          </div>
          <span className="tripper-group__footer-text">
            <span className="tripper-group__footer-prefix">End of </span>
            <span className="tripper-group__footer-group-name">{groupName}</span>
          </span>
        </div>
        {footerDistance ? (
          <div className="tripper-group__footer-distance">
            {footerDistance}
          </div>
        ) : null}
      </div>
    </div>
  );
};
